import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, useTheme, Fab, Divider } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import CreateSubmissionDialog from '../components/CreateSubmissionDialog';
import { isSubmissionCompletedBucket } from 'services/Utilities/submissionUtils';
import CertificationPageHero from '../components/CertificationPageHero';
import SubmissionCard from './components/SubmissionCard';
import { useSubmissionsByDealer } from 'services/Data/SubmissionServices';

const Submissions = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const account = useSelector(state => state.AccountReducer);
  const location = useLocation();
  const history = useHistory();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const dealerId = account?.profile?.dealer_Id || account?.profile?.id;
  const { data: submissions, isLoading, error } = useSubmissionsByDealer(dealerId);

  const { activeSubmissions, completedSubmissions } = useMemo(() => {
    if (!Array.isArray(submissions)) return { activeSubmissions: [], completedSubmissions: [] };
    return submissions.reduce(
      (acc, submission) => {
        if (isSubmissionCompletedBucket(submission)) acc.completedSubmissions.push(submission);
        else acc.activeSubmissions.push(submission);
        return acc;
      },
      { activeSubmissions: [], completedSubmissions: [] },
    );
  }, [submissions]);

  useEffect(() => {
    if (!location.hash) return;
    const timeoutId = window.setTimeout(() => {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [location.hash, activeSubmissions.length, completedSubmissions.length]);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress sx={{ color: theme.palette.secondary.main }} />
        <Typography sx={{ color: theme.palette.text.secondary, mt: 2 }}>{t('submissions.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('submissions.loadingFailed')} {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <CertificationPageHero
        image="/images/certification/carousel-img-en-01.jpg"
        title={t('carousel.certificationTitle')}
        subtitle={t('carousel.certificationSubtitle')}
      />
      <Box
        sx={{
          maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
            {t('submissions.activeSubmissions')}
          </Typography>
          {activeSubmissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('submissions.noneActive')}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 3,
              }}>
              {activeSubmissions.map(sub => (
                <SubmissionCard key={sub.id} submission={sub} />
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box id="completed-submissions-list" sx={{ mb: 2 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
            {t('submissions.completedSubmissions')}
          </Typography>
          {completedSubmissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('submissions.noneCompleted')}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 3,
              }}>
              {completedSubmissions.map(sub => (
                <SubmissionCard key={sub.id} submission={sub} />
              ))}
            </Box>
          )}
        </Box>

        <Fab
          color="secondary"
          size="medium"
          aria-label={t('submissions.createTitle')}
          onClick={() => setShowCreateDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: theme.zIndex.tooltip,
            boxShadow: theme.shadows[8],
          }}>
          <AddIcon />
        </Fab>
      </Box>
      <CreateSubmissionDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        existingSubmissions={submissions}
        onNavigate={path => history.push(path)}
      />
    </Box>
  );
};

export default Submissions;
